import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Animate, Box, Card, Flex, Image, Text, ChatMessage, RatingBadge, Relative, Button } from 'pcln-design-system';
import { HotelFilterComponent } from './HotelFilterComponent';
import styled from 'styled-components';
import { TravelBotLogoImage } from '../TravelBotLogoImage';
import { transformSOPQName } from '../utils';
import analytics from '../analytics';
import MessageFooter from '../components/MessageFooter/MessageFooter';
import { ChatbotDataContext } from '../context/ChatbotDataContext';
import { generateHotelSummary } from './generateHotelSummary';
const StyledImage = /*#__PURE__*/styled(Image).withConfig({
  displayName: "HotelDetailCard__StyledImage",
  componentId: "sc-iimai3-0"
})(["border-bottom-left-radius:", ";border-top-left-radius:16px;object-fit:cover;"], props => props.displayGenHotelFeaturesMobile ? '0' : '16px');
const CustomTruncate = /*#__PURE__*/styled(Text).withConfig({
  displayName: "HotelDetailCard__CustomTruncate",
  componentId: "sc-iimai3-1"
})(["width:100%;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;"]);
const DetailCard = /*#__PURE__*/styled(Card).withConfig({
  displayName: "HotelDetailCard__DetailCard",
  componentId: "sc-iimai3-2"
})(["cursor:pointer;width:--webkit-fill-available;"]);
const sopqImages = ['https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-2.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-4-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-2-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-4-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp', 'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-2-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp'];
export const HotelDetailCard = props => {
  var _a;
  const {
    isMobile,
    setiValues,
    fetchRequestOptions,
    prompts
  } = useContext(ChatbotDataContext);
  const {
    starRating,
    location,
    pclnPropertyTypeId,
    ratesSummary,
    hotelType,
    description
  } = props;
  const {
    grandTotal,
    nightlyRateIncludingTaxesAndFees,
    minCurrencyCodeSymbol,
    priceDisplayRegulation,
    minPrice
  } = ratesSummary || {};
  const transformSopqNameArgs = {
    starRating,
    location,
    pclnPropertyTypeId
  };
  const isExpressDeal = hotelType === 'SOPQ';
  const detailURL = `/relax/at/${isExpressDeal ? `express/${(_a = props.location) === null || _a === void 0 ? void 0 : _a.cityId}/${props.pclnId}` : props.hotelId}/from/${props === null || props === void 0 ? void 0 : props['check-in']}/to/${props === null || props === void 0 ? void 0 : props['check-out']}/rooms/${props.rooms}/adults/${props === null || props === void 0 ? void 0 : props['adult-occ']}`;
  const sopqImageIndex = useMemo(() => {
    return Math.floor(Math.random() * sopqImages.length);
  }, []);
  const showHotelPrice = priceDisplayRegulation === 'TOTAL_PRICE_PROMINENT' ? minCurrencyCodeSymbol && !isNaN(nightlyRateIncludingTaxesAndFees) && !isNaN(grandTotal) : minCurrencyCodeSymbol && !isNaN(minPrice) && !isNaN(grandTotal);
  const nightlyRate = priceDisplayRegulation === 'TOTAL_PRICE_PROMINENT' ? nightlyRateIncludingTaxesAndFees : minPrice;
  const isCompact = window.innerWidth < 406;
  const propertyName = isExpressDeal && !props.name ? transformSOPQName(transformSopqNameArgs) : props.name;
  const [summary, setSummary] = useState('');
  const [hasSummaryError, setHasSummaryError] = useState(false);
  const [isSummaryLoading, setSummaryIsLoading] = useState(false);
  const shouldDisplayFeatureSummary = (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplayGenHotelFeature) && summary && !hasSummaryError;
  const displayGenHotelFeaturesMobile = isMobile && shouldDisplayFeatureSummary;
  const displayGenHotelFeaturesDesktop = !isMobile && shouldDisplayFeatureSummary;
  useEffect(() => {
    var _a, _b, _c, _d;
    if (!isExpressDeal && (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplayGenHotelFeature)) {
      generateHotelSummary({
        prompts,
        fetchRequestOptions,
        hotelDetail: {
          hotelName: propertyName,
          description,
          starRating,
          hotelFeatures: (_b = (_a = props.hotelFeatures) === null || _a === void 0 ? void 0 : _a.hotelAmenities) === null || _b === void 0 ? void 0 : _b.map(feature => feature.name),
          petPolicy: (_c = props === null || props === void 0 ? void 0 : props.policies) === null || _c === void 0 ? void 0 : _c.petDescription
        },
        otherOptions: (_d = props === null || props === void 0 ? void 0 : props.otherOptions) === null || _d === void 0 ? void 0 : _d.map(({
          name,
          description,
          hotelFeatures,
          policies
        }) => {
          var _a;
          return {
            hotelName: name,
            description,
            petPolicy: policies === null || policies === void 0 ? void 0 : policies.petDescription,
            hotelFeatures: (_a = hotelFeatures === null || hotelFeatures === void 0 ? void 0 : hotelFeatures.hotelAmenities) === null || _a === void 0 ? void 0 : _a.map(feature => feature.name)
          };
        }),
        setSummaryText: summaryText => setSummary(summaryText),
        setSummaryIsLoading: isloading => {
          setSummaryIsLoading(isloading);
        },
        setHasSummaryError: hasError => setHasSummaryError(hasError)
      }).catch(err => {
        analytics.logError({
          message: 'Error generating hotel summary',
          error: JSON.stringify(err),
          loggedFrom: 'HotelDetailCard'
        });
        setHasSummaryError(true);
        setSummaryIsLoading(false);
      });
    }
  }, [propertyName]);
  return React.createElement(Animate, {
    key: props.hotelIndex,
    variant: 'slideInLeft',
    override: {
      transition: {
        delay: 0.1 * props.hotelIndex || 0
      }
    }
  }, React.createElement(DetailCard, {
    m: [0, 3],
    mb: 3,
    bg: 'white',
    borderRadius: 'xl',
    onClick: function () {
      window.open(detailURL, '_blank');
    }
  }, React.createElement(Flex, {
    width: 1
  }, React.createElement(Box, {
    width: [3 / 10, '90px', '150px']
  }, React.createElement(Relative, {
    height: '100%',
    width: [1, '90px', '150px']
  }, React.createElement(StyledImage, {
    src: isExpressDeal ? sopqImages[sopqImageIndex] : props.thumbnailUrl,
    width: [1, '90px', '150px'],
    height: '100%',
    displayGenHotelFeaturesMobile: displayGenHotelFeaturesMobile
  }))), React.createElement(Flex, {
    width: [7 / 10, 1],
    my: 2
  }, React.createElement(Flex, {
    width: 1,
    ml: 2
  }, React.createElement(Flex, {
    flexDirection: 'column',
    width: 6 / 10
  }, React.createElement(Box, null, React.createElement(CustomTruncate, {
    fontSize: [0, 1],
    fontWeight: 'bold',
    lineHeight: 1.4
  }, propertyName)), React.createElement(Flex, {
    flexDirection: ['column', 'row'],
    alignItems: ['none', 'center']
  }, React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(Text, {
    pb: 1
  }, React.createElement(RatingBadge, {
    size: 'small',
    padding: 0,
    minWidth: '32px',
    height: '20px'
  }, React.createElement(Text, {
    fontSize: 0,
    lineHeight: '20px',
    textAlign: 'center',
    pb: 1
  }, props.overallGuestRating))), !isCompact ? React.createElement(Text, {
    pl: [1],
    fontSize: 0,
    lineHeight: '20px',
    style: {
      whiteSpace: 'nowrap'
    }
  }, props.totalReviewCount, " Reviews") : null, React.createElement(Text, {
    pl: [1],
    fontSize: 0
  }, "|")), React.createElement(Flex, null, React.createElement(Text, {
    pl: [0, 1],
    fontSize: 0,
    lineHeight: '20px'
  }, starRating, "-Star Hotel"))), displayGenHotelFeaturesDesktop && React.createElement(Text, {
    fontSize: 0,
    lineHeight: '20px',
    pb: 1
  }, summary, isSummaryLoading ? ' ... ' : ''), React.createElement(Box, null, React.createElement(Button, {
    variation: 'link',
    onClick: function () {
      analytics.log(Object.assign({
        label: 'HotelSearchResult',
        hotelId: props.hotelId,
        message: 'HOTEL_DETAIL_LINK_CLICKED'
      }, props === null || props === void 0 ? void 0 : props.args));
      window.open(detailURL, '_blank');
    }
  }, React.createElement(Text, {
    bold: true,
    fontSize: 0
  }, isCompact ? ' View Details' : 'View Hotel Details')))), React.createElement(Flex, {
    width: 4 / 10,
    mr: 2,
    alignItems: 'center'
  }, showHotelPrice && React.createElement(Flex, {
    width: 1,
    flexDirection: 'column',
    alignItems: 'flex-end'
  }, React.createElement(Text, {
    fontSize: 0
  }, "From"), React.createElement(Flex, null, React.createElement(Text, {
    textStyle: 'caption',
    color: 'pricePrimary'
  }, minCurrencyCodeSymbol), React.createElement(Text, {
    bold: true,
    fontSize: 4,
    color: 'pricePrimary'
  }, Math.round(nightlyRate))), React.createElement(Text, {
    fontSize: 0
  }, "Total: ", minCurrencyCodeSymbol, Math.round(grandTotal))))))), displayGenHotelFeaturesMobile && React.createElement(Card, {
    m: 2,
    p: 2,
    borderRadius: 'xl'
  }, React.createElement(Text, {
    fontSize: 0,
    lineHeight: '15px'
  }, summary, isSummaryLoading ? ' ... ' : ''))));
};
export const HotelDetailCardsMessage = props => {
  var _a, _b, _c, _d, _e, _f, _g;
  useEffect(() => {
    analytics.log(Object.assign({
      label: 'HotelSearchResult',
      message: 'HOTEL_CARDS_DISPLAY'
    }, props === null || props === void 0 ? void 0 : props.args));
  }, []);
  const highRatedHotels = (_b = (_a = props.searchResult) === null || _a === void 0 ? void 0 : _a.hotels) === null || _b === void 0 ? void 0 : _b.filter(hotel => hotel.overallGuestRating > 7.5);
  const displayResult = (highRatedHotels === null || highRatedHotels === void 0 ? void 0 : highRatedHotels.length) ? (_e = (_d = (_c = props.searchResult) === null || _c === void 0 ? void 0 : _c.hotels) === null || _d === void 0 ? void 0 : _d.filter(hotel => hotel.overallGuestRating > 7.5)) === null || _e === void 0 ? void 0 : _e.slice(0, 3) : (_g = (_f = props.searchResult) === null || _f === void 0 ? void 0 : _f.hotels) === null || _g === void 0 ? void 0 : _g.slice(0, 3);
  return React.createElement(React.Fragment, null, React.createElement(ChatMessage, {
    mb: '8px',
    ml: '',
    variation: 'initial',
    Icon: TravelBotLogoImage,
    message: React.createElement(Box, null, React.createElement(Text, {
      pb: 3
    }, "Here are some of the top guest-rated ", (props === null || props === void 0 ? void 0 : props.args.isSOPQOnly) ? 'express deals' : 'hotels', " for your consideration:"), displayResult === null || displayResult === void 0 ? void 0 : displayResult.map((hotel, index) => React.createElement(Box, {
      key: String(hotel.hotelId),
      width: 1
    }, React.createElement(HotelDetailCard, Object.assign({}, hotel, props === null || props === void 0 ? void 0 : props.args, {
      isMobile: props.isMobile,
      args: props === null || props === void 0 ? void 0 : props.args,
      hotelIndex: index,
      otherOptions: displayResult.filter(h => h.name !== hotel.name)
    }))))),
    footer: React.createElement(MessageFooter, {
      date: props.date
    })
  }), React.createElement(HotelFilterComponent, Object.assign({}, props)));
};